import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import styles from "./AuthCallback.module.css";

export default function AuthCallback() {
  const navigate  = useNavigate();
  const [status,  setStatus]  = useState("verifying"); // verifying | set-password | error
  const [password, setPassword] = useState("");
  const [confirm,  setConfirm]  = useState("");
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState("");

  useEffect(() => {
    handleCallback();
  }, []); // eslint-disable-line

  async function handleCallback() {
    try {
      // Supabase puts the token in the URL hash or query params
      // The supabase client handles extracting it automatically
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) throw error;

      if (session) {
        // Session established — check if this is a first-time invite
        // (user has no password set yet — they came from an invite link)
        const hash   = window.location.hash;
        const params = new URLSearchParams(window.location.search);
        const type   = params.get("type") || extractFromHash(hash, "type");

        if (type === "invite" || type === "recovery") {
          // Show password set form
          setStatus("set-password");
        } else {
          // Already has session, go to dashboard
          navigate("/dashboard");
        }
      } else {
        // No session yet — try to exchange token from URL
        const hash   = window.location.hash;
        const params = new URLSearchParams(
          hash.startsWith("#") ? hash.slice(1) : window.location.search
        );

        const accessToken  = params.get("access_token");
        const refreshToken = params.get("refresh_token");
        const type         = params.get("type");

        if (accessToken && refreshToken) {
          const { error: sessionError } = await supabase.auth.setSession({
            access_token:  accessToken,
            refresh_token: refreshToken,
          });

          if (sessionError) throw sessionError;

          if (type === "invite" || type === "recovery") {
            setStatus("set-password");
          } else {
            navigate("/dashboard");
          }
        } else {
          throw new Error("Invalid or expired link. Please ask your administrator to resend the invite.");
        }
      }
    } catch (err) {
      setError(err.message);
      setStatus("error");
    }
  }

  function extractFromHash(hash, key) {
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    return params.get(key);
  }

  async function handleSetPassword(e) {
    e.preventDefault();
    setError("");

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }

    setSaving(true);

    const timeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Request timed out. Try again.")), 15000)
    );

    try {
      const { error } = await Promise.race([
        supabase.auth.updateUser({ password }),
        timeout,
      ]);

      if (error) throw error;

      // Password set successfully — go to dashboard
      navigate("/dashboard");
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  // ── Verifying ──
  if (status === "verifying") {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.spinner} />
          <p className={styles.message}>Verifying your link…</p>
        </div>
      </div>
    );
  }

  // ── Error ──
  if (status === "error") {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.errorIcon}>⚠️</div>
          <h2 className={styles.heading}>Link Invalid or Expired</h2>
          <p className={styles.sub}>{error}</p>
          <p className={styles.sub} style={{ marginTop: "0.5rem" }}>
            Please contact your administrator to resend the invite.
          </p>
          <button
            className={styles.btn}
            onClick={() => navigate("/login")}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  // ── Set password ──
  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <div className={styles.brandIcon}>RH</div>
          <div>
            <div className={styles.brandName}>Royal Horizon</div>
            <div className={styles.brandSub}>Management Portal</div>
          </div>
        </div>

        <h2 className={styles.heading}>Set Your Password</h2>
        <p className={styles.sub}>
          Choose a strong password to secure your account.
          You will use this to log in every time.
        </p>

        <form onSubmit={handleSetPassword} className={styles.form}>
          <div className={styles.field}>
            <label>New Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              required
              autoFocus
            />
          </div>

          <div className={styles.field}>
            <label>Confirm Password</label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Re-enter your password"
              required
            />
          </div>

          {/* Password strength indicator */}
          {password.length > 0 && (
            <div className={styles.strengthWrap}>
              <div className={styles.strengthBar}>
                <div
                  className={styles.strengthFill}
                  style={{
                    width:      `${Math.min((password.length / 16) * 100, 100)}%`,
                    background: password.length < 8  ? "#c53030"
                              : password.length < 12 ? "#c05621"
                              : "#276749",
                  }}
                />
              </div>
              <span className={styles.strengthLabel}>
                {password.length < 8  ? "Too short"
               : password.length < 12 ? "Good"
               : "Strong"}
              </span>
            </div>
          )}

          {error && <div className={styles.error}>{error}</div>}

          <button
            type="submit"
            className={styles.btn}
            disabled={saving}
          >
            {saving ? "Setting password…" : "Set Password & Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}